import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = user, false = guest
  const [loading, setLoading] = useState(true);

  // Deps intentionally empty: runs once on mount to bootstrap auth from the
  // /auth/me endpoint. `api` is a stable module-level axios instance; the
  // setters and the closure-local `cancelled` flag are not reactive values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch (err) {
        if (err?.response?.status !== 401 && process.env.NODE_ENV !== "production") {
          console.error("Auth check failed:", err);
        }
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Deps intentionally empty: `api` is a module-level singleton, setUser is
  // guaranteed stable by React.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  // Deps intentionally empty: same rationale as `login`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Logout request failed:", err);
      }
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
